import { useCallback, useEffect, useState, useMemo } from 'react';

import debounce from 'lodash/debounce';
import { useField } from 'react-final-form';

import { SuggestedInput as Input } from '@vk-hr-tek/ui/input';

import { compose } from '../validation';

import { SuggestedInputProps } from './SuggestedInput.types';

/**
 * Компонент ввода с подсказками.
 *
 * @public
 * @param type - Тип ввода: 'load' для загрузки с сервера, 'local' для локального списка.
 * @param name - Имя поля формы.
 * @param label - Текст метки для поля формы.
 * @param labelIcon - Иконка для метки.
 * @param onChange - Дополнительная функция-обработчик изменения значения.
 * @param placeholder - Плейсхолдер для поля ввода.
 * @param disabled - Флаг, указывающий, отключено ли поле.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param clearable - Флаг, указывающий, можно ли очистить поле.
 * @param loading - Флаг, указывающий, находится ли поле в состоянии загрузки.
 * @param tooltip - Тултип для поля формы.
 * @param helperText - Вспомогательный текст под полем.
 * @param renderOption - Функция для кастомного рендеринга опции.
 * @param limit - Максимальное количество отображаемых элементов.
 * @param minInputValueLength - Минимальная длина введённого значения для отображения подсказок.
 * @param mask - Маска для поля ввода.
 * @param validate - Правила валидации.
 * @param validationWithoutTouch - Флаг, указывающий, нужно ли показывать ошибку без касания поля.
 * @param validationWhenDisabled - Флаг, указывающий, нужно ли валидировать поле, когда оно отключено.
 * @param debounceTimeout - Таймаут дебаунса для обработчика изменения.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Компонент Field, который рендерит кастомный компонент ввода Input.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SuggestedInputProps}
 */
export const SuggestedInput = (props: SuggestedInputProps) => {
  const {
    type,
    name,
    label,
    labelIcon,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    debounceTimeout,
    ...rest
  } = props;

  const fieldProps = useField<string | undefined>(name, {
    validate: Array.isArray(validate) ? compose(validate) : validate,
    ...rest,
  });

  const { input, meta } = fieldProps;
  const { onChange: onInputChange } = input;

  const [value, setValue] = useState<string | undefined>(input.value);

  const handleChangeForm = useCallback(
    (formValue?: string) => {
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  useEffect(() => {
    return () => {
      handleChangeDebounce.flush();
    };
  }, [handleChangeDebounce]);

  const handleChange = useCallback(
    (changedValue?: string) => {
      setValue(changedValue);

      if (debounceTimeout) {
        handleChangeDebounce(changedValue);

        if (changedValue === '') {
          handleChangeForm(changedValue);
        }
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  const commonInputProps = {
    ...input,
    type,
    label,
    labelIcon,
    onChange: handleChange,
    placeholder,
    disabled,
    required,
    clearable,
    loading,
    tooltip,
    helperText: (renderError && meta.error) || helperText,
    renderOption,
    limit,
    minInputValueLength,
  };

  if (type === 'load') {
    return (
      <Input
        {...commonInputProps}
        value={value || input.value}
        type="load"
        loadItems={props.loadItems}
        mask={mask}
        error={renderError}
      />
    );
  }

  return (
    <Input
      {...commonInputProps}
      value={value || input.value}
      type="local"
      externalItems={props.externalItems}
      error={meta.touched && !!meta.error}
    />
  );
};
